import { Avatar, Box, Button, Checkbox, IconButton, Typography } from "@material-ui/core";
import { CircularProgress, Modal } from "@mui/material";
import {
  ArrowBendDownLeft,
  ArrowBendDownRight,
  Check,
  Clock,
  FilmSlate,
  PaintBrush,
  PaperPlaneTilt,
  PencilSimple,
  X
} from "phosphor-react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { fget, fpost } from "../../../API/callsAPI";
import { MovieColabContext } from "../../../Context/MovieColabContext";
import PostEditor from "../../PostEditor/postEditor";
import useCommentsPanelStyles from "./SequencerMixCommentsPanel.styles";
import CanvasFrame from "../../CanvasFrame/CanvasFrame.component";

const getDisplayTime = (time) => {
  if (time === null || time === undefined || time === "") return "";
  if (String(time).includes(":")) {
    return time;
  }
  const totalSeconds = Number(time);
  if (Number.isNaN(totalSeconds)) return "";
  const h = Math.floor(totalSeconds / 3600)
    .toString()
    .padStart(2, "0");
  const m = Math.floor((totalSeconds % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(totalSeconds % 60)
    .toString()
    .padStart(2, "0");
  return `${h}:${m}:${s}`;
};

const formatSequencerTime = (seconds) => {
  if (Number.isNaN(seconds)) return "00:00:00";
  return getDisplayTime(seconds);
};

const getDraftStorageKey = (timelineId) =>
  `sequencer-playlist-drafts-${timelineId}`;

const organizeCommentsWithReplies = (comments = []) => {
  const repliesByParent = {};
  const topLevel = [];

  comments.forEach((comment) => {
    const parent = comment.replied_to
      ? comments.find((item) => String(item.id) === String(comment.replied_to))
      : null;

    if (parent) {
      if (!repliesByParent[parent.id]) {
        repliesByParent[parent.id] = [];
      }
      repliesByParent[parent.id].push(comment);
      return;
    }

    topLevel.push(comment);
  });

  Object.values(repliesByParent).forEach((replies) => {
    replies.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  });

  return topLevel
    .map((comment) => ({
      ...comment,
      replies: repliesByParent[comment.id] || [],
    }))
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
};

const SequencerMixPlaylistCommentsPanel = ({
  selectedVersion,
  currentTime,
  setCurrentTime,
  setSeekToTime,
  isVideoPlaying,
  projectId,
  setScreenshot,
  screenshot,
  canvasModal,
  setCanvasModal,
  sequencerDetails,
  shotsLineUp,
}) => {
  const { loggedInUser } = useContext(MovieColabContext);
  const classes = useCommentsPanelStyles();
  const [apiComments, setApiComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTimeStampChecked, setIsTimeStampChecked] = useState(false);
  const [isSaveAsDraft, setIsSaveAsDraft] = useState(false);
  const [resetCommentText, setResetCommentText] = useState(0);
  const [drafts, setDrafts] = useState([]);
  const [publishingDraftId, setPublishingDraftId] = useState(null);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [editingDraftText, setEditingDraftText] = useState("");
  const [repliedTo, setRepliedTo] = useState(null);
  const [expandedReplies, setExpandedReplies] = useState({});
  const mentionsRef = useRef(null);
  const [attachmentModal, setAttachmentModal] = useState(false);
  const [attachment, setAttachment] = useState(null);


  const mentionUsers = useMemo(
    () =>
      (sequencerDetails?.allowed_users || []).map((user) => ({
        name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        id: user.id,
      })),
    [sequencerDetails?.allowed_users],
  );

  const getTimeStamp = () => {
    return currentTime > 0 ? Math.floor(currentTime) : "";
  };

  const getShotDisplayName = (lineupItem, shotId) =>
    lineupItem?.shotVersion?.shot?.code ||
    lineupItem?.shotVersion?.shot?.name ||
    `Shot ${shotId}`;

  const groupCommentsByShot = (comments = []) => {
    const organizedComments = organizeCommentsWithReplies(comments);
    const shotReprToShotId = {};
    const shotInfoByShotId = {};

    (shotsLineUp || []).forEach((lineupItem) => {
      const shotId = lineupItem?.shot;
      if (shotId == null) return;

      shotReprToShotId[lineupItem.id] = shotId;

      if (!shotInfoByShotId[shotId]) {
        shotInfoByShotId[shotId] = {
          id: shotId,
          label: getShotDisplayName(lineupItem, shotId),
        };
      }
    });

    const commentsByShotId = organizedComments.reduce((acc, comment) => {
      const shotId = shotReprToShotId[comment.shot_repr];
      if (shotId == null) return acc;

      if (!acc[shotId]) {
        acc[shotId] = [];
      }
      acc[shotId].push(comment);
      return acc;
    }, {});

    const seenShotIds = new Set();
    const orderedShotIds = [];

    (shotsLineUp || []).forEach((lineupItem) => {
      const shotId = lineupItem?.shot;
      if (shotId == null || seenShotIds.has(shotId)) return;
      seenShotIds.add(shotId);
      orderedShotIds.push(shotId);
    });

    return orderedShotIds
      .map((shotId) => ({
        shot: shotInfoByShotId[shotId],
        comments: commentsByShotId[shotId] || [],
      }))
      .filter((group) => group.comments.length > 0);
  };

  const groupedComments = useMemo(
    () => groupCommentsByShot([...apiComments, ...drafts]),
    [apiComments, drafts, shotsLineUp],
  );

  const totalCommentCount = groupedComments.reduce(
    (count, group) =>
      count +
      group.comments.reduce(
        (groupCount, comment) => groupCount + 1 + (comment.replies?.length || 0),
        0,
      ),
    0,
  );

  const toggleReplies = (commentId) => {
    setExpandedReplies((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const handleSeekToTimestamp = (timestamp) => {
    const seekTime = Number(timestamp);
    if (Number.isNaN(seekTime) || seekTime < 0 || !setSeekToTime || !setCurrentTime) {
      return;
    }

    setSeekToTime(seekTime);
    setCurrentTime(seekTime);
  };

  const fetchPlaylistComments = async () => {
    try {
      setIsLoading(true);
      const comments = await fget({
        url: `/sequencers/playlist-comments/?timeline=${sequencerDetails?.id}`,
      });
      setApiComments(comments.data.results || []);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sequencerDetails?.id && shotsLineUp?.length) {
      fetchPlaylistComments();
      const storedDrafts =
        JSON.parse(localStorage.getItem(getDraftStorageKey(sequencerDetails.id))) ||
        [];
      setDrafts(storedDrafts);
    }
  }, [sequencerDetails?.id, shotsLineUp]);

  const postPlaylistComment = async ({
    shot_repr,
    description,
    timestamp,
    replied_to,
    mentions = [],
  }) => {
    const payload = {
      shot_repr,
      description: description.trim(),
    };

    if (timestamp) {
      payload.timestamp = timestamp;
    }

    if (replied_to) {
      payload.replied_to = replied_to;
    }

    if (mentions.length) {
      payload.mentions = mentions;
    }

    return fpost({
      url: `/sequencers/playlist-comments/`,
      data: payload,
    });
  };

  const resetCommentEditor = () => {
    mentionsRef.current?.reset();
    setResetCommentText((prev) => prev + 1);
  };

  const getCommentPayloadFromEditor = () => {
    const description = mentionsRef.current?.toHtml();
    if (!description) return null;

    return {
      description,
      mentions: mentionsRef.current?.onExtractMentions() || [],
    };
  };

  const clearReplyState = () => {
    setRepliedTo(null);
    resetCommentEditor();
    setIsTimeStampChecked(false);
  };
  const handleStartReply = (comment) => {
    if (comment.isDraft) return;
    setRepliedTo(comment);
    setIsSaveAsDraft(false);
    setEditingDraftId(null);
    setEditingDraftText("");
  };

  const handleMakeComment = async () => {
    const editorPayload = getCommentPayloadFromEditor();
    if (!editorPayload) return;

    const shotRepr = repliedTo?.shot_repr ?? selectedVersion?.id;
    if (!shotRepr) return;

    try {
      const comment = await postPlaylistComment({
        shot_repr: shotRepr,
        description: editorPayload.description,
        mentions: editorPayload.mentions,
        timestamp: isTimeStampChecked ? getTimeStamp() || 0 : undefined,
        replied_to: repliedTo?.id,
      });

      if (comment.status === 200 || comment.status === 201) {
        if (repliedTo?.id) {
          setExpandedReplies((prev) => ({ ...prev, [repliedTo.id]: true }));
        }
        clearReplyState();
        fetchPlaylistComments();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handlePublishDraft = async (draft) => {
    if (!draft?.description?.trim() || !draft?.shot_repr) return;

    try {
      setPublishingDraftId(draft.id);
      const comment = await postPlaylistComment({
        shot_repr: draft.shot_repr,
        description: draft.description,
        timestamp: draft.timestamp,
        replied_to: draft.replied_to,
        mentions: draft.mentions || [],
      });
      if (comment.status === 200 || comment.status === 201) {
        handleDeleteDraft(draft.id);
        fetchPlaylistComments();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setPublishingDraftId(null);
    }
  };

  const persistDrafts = (updatedDrafts) => {
    localStorage.setItem(
      getDraftStorageKey(sequencerDetails.id),
      JSON.stringify(updatedDrafts),
    );
    setDrafts(updatedDrafts);
  };

  const handleSaveAsDraft = () => {
    if (repliedTo || !selectedVersion?.id || !sequencerDetails?.id) {
      return;
    }

    const editorPayload = getCommentPayloadFromEditor();
    if (!editorPayload) return;

    try {
      const payload = {
        id: `draft-${Date.now()}`,
        shot_repr: selectedVersion.id,
        description: editorPayload.description,
        mentions: editorPayload.mentions,
        isDraft: true,
        created_at: new Date().toISOString(),
        timeline: sequencerDetails.id,
        author: {
          first_name: loggedInUser?.first_name || "You",
          last_name: loggedInUser?.last_name || "",
        },
      };

      if (isTimeStampChecked) {
        payload.timestamp = getTimeStamp() || 0;
      }

      const updatedDrafts = [...drafts, payload];
      persistDrafts(updatedDrafts);
      resetCommentEditor();
      setIsTimeStampChecked(false);
      setIsSaveAsDraft(false);
    } catch (error) {
      console.log(error);
    }
  };
  const handleDeleteDraft = (draftId) => {
    const updatedDrafts = drafts.filter((draft) => draft.id !== draftId);
    persistDrafts(updatedDrafts);
    if (editingDraftId === draftId) {
      setEditingDraftId(null);
      setEditingDraftText("");
    }
  };

  const handleStartEditDraft = (draft) => {
    setEditingDraftId(draft.id);
    setEditingDraftText(draft.description || "");
  };

  const handleSaveEditDraft = () => {
    if (!editingDraftId || !editingDraftText.trim()) return;

    const updatedDrafts = drafts.map((draft) => {
      if (draft.id !== editingDraftId) return draft;

      return {
        ...draft,
        description: editingDraftText.trim(),
        created_at: new Date().toISOString(),
      };
    });

    persistDrafts(updatedDrafts);
    setEditingDraftId(null);
    setEditingDraftText("");
  };

  const handleCancelEditDraft = () => {
    setEditingDraftId(null);
    setEditingDraftText("");
  };

  return (
    <Box className={classes.content}>
      <Box className={classes.toolbar}>
        <Typography className={classes.commentCount}>
          {totalCommentCount} comment{totalCommentCount === 1 ? "" : "s"}
        </Typography>
        {/* <Button
          className={classes.publishButton}
          startIcon={<EnvelopeSimple size={14} />}
          disableRipple
        >
          Publish Summary
        </Button> */}
      </Box>

      {isLoading ?
        <Box className={classes.commentsList} style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <CircularProgress size={40} color="primary" />
        </Box>
        : <Box className={classes.commentsList}>
          {groupedComments.map((group) => (
            <Box key={group.shot.id}>
              <Box className={classes.groupHeader}>
                <FilmSlate size={18} />
                <span className={classes.groupName}>
                  {group.shot.label}
                </span>
                <Box className={classes.groupDivider} />
              </Box>

              {group.comments.map((comment) => (
                <Box key={comment.id} className={classes.commentItem}>
                  {comment.isDraft && (
                    <Box className={classes.commentHead} style={{ marginBottom: "8px" }}>
                      <span className={classes.draftBadge}>Draft</span>
                      <Box className={classes.draftActions}>
                        {editingDraftId === comment.id ? (
                          <>
                            <Button
                              className={classes.draftActionButtonFilled}
                              disableRipple
                              onClick={handleSaveEditDraft}
                            >
                              <Check size={18} />
                            </Button>
                            <Button
                              className={classes.draftActionButtonOutlined}
                              disableRipple
                              onClick={handleCancelEditDraft}
                            >
                              <X size={18} />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              className={classes.draftActionButtonOutlined}
                              disableRipple
                              disabled={!!editingDraftId}
                              onClick={() => handleStartEditDraft(comment)}
                            >
                              <PencilSimple size={18} />
                            </Button>
                            <Button
                              className={classes.draftActionButtonOutlined}
                              disableRipple
                              disabled={!!editingDraftId}
                              onClick={() => handleDeleteDraft(comment.id)}
                            >
                              <X size={18} />
                            </Button>
                            <Button
                              className={classes.draftActionButtonFilled}
                              disableRipple
                              disabled={
                                !!editingDraftId || publishingDraftId === comment.id
                              }
                              onClick={() => handlePublishDraft(comment)}
                            >
                              <Check size={18} />
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                  )}
                  <Box className={classes.commentHead}>
                    <Box className={classes.commentAuthor}>
                      <Avatar className={classes.avatar}>
                        {comment.author?.first_name?.[0]?.toUpperCase() || "?"}
                        {comment.author?.last_name?.[0]?.toUpperCase() || ""}
                      </Avatar>
                      <span className={classes.authorName}>
                        {comment.author?.first_name || "Unknown"}{" "}
                        {comment.author?.last_name || ""}
                      </span>
                    </Box>
                    <Typography className={classes.commentTime}>
                      {comment.created_at
                        ? new Date(comment.created_at).toLocaleString()
                        : ""}
                    </Typography>
                  </Box>
                  {comment.attachment && <><img style={{ cursor: "pointer", height: "150px", width: "230px", marginTop: "5px", objectFit: "contain" }} src={comment.attachment} alt="screenshot" onClick={() => { setAttachmentModal(true); setAttachment(comment.attachment); }} /><br /></>}
                  <Box className={classes.commentBody}>
                    {comment.timestamp && (
                      <span
                        className={classes.seekableTimestamp}
                        onClick={() => handleSeekToTimestamp(comment.timestamp)}
                      >
                        {getDisplayTime(comment.timestamp)}
                      </span>
                    )}
                    {comment.isDraft && editingDraftId === comment.id ? (
                      <textarea
                        className={classes.draftCommentInput}
                        value={editingDraftText}
                        onChange={(e) => setEditingDraftText(e.target.value)}
                        placeholder="Edit draft comment..."
                      />
                    ) : (
                      <span
                        className={classes.commentText}
                        dangerouslySetInnerHTML={{ __html: comment.description }}
                      />
                    )}
                  </Box>

                  {!comment.isDraft && <Box className={classes.commentFooter}>
                    <Box className={classes.commentFooterLeft}>
                      {comment.replies?.length ? (
                        <Button
                          className={classes.showHideRepliesButton}
                          onClick={() => toggleReplies(comment.id)}
                          disableRipple
                        >
                          {expandedReplies[comment.id]
                            ? "Hide Replies"
                            : `Show ${comment.replies.length} ${comment.replies.length === 1 ? "Reply" : "Replies"
                            }`}
                        </Button>
                      ) : (
                        <span className={classes.repliesLabel}>No replies</span>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      disabled={!!editingDraftId}
                      onClick={() => handleStartReply(comment)}
                    >
                      <ArrowBendDownLeft size={18} color="#606479" />
                    </IconButton>
                  </Box>}

                  {comment.replies?.length > 0 && expandedReplies[comment.id] && (
                    <Box className={classes.repliesList}>
                      {comment.replies.map((reply) => (
                        <Box key={reply.id} className={classes.replyItem}>
                          <Box className={classes.replyHead}>
                            <ArrowBendDownRight size={18} color="#606479" />
                            <Avatar className={classes.avatar}>
                              {reply.author?.first_name?.[0] || "?"}
                              {reply.author?.last_name?.[0] || ""}
                            </Avatar>
                            <span className={classes.authorName}>
                              {reply.author?.first_name || "Unknown"}{" "}
                              {reply.author?.last_name || ""}
                            </span>
                            <span className={classes.commentTime}>
                              {reply.created_at
                                ? new Date(reply.created_at).toLocaleString()
                                : ""}
                            </span>
                          </Box>
                          <Box className={classes.replyBody}>
                            {reply.timestamp ? (
                              <span
                                className={classes.seekableTimestamp}
                                onClick={() => handleSeekToTimestamp(reply.timestamp)}
                              >
                                {getDisplayTime(reply.timestamp)}
                              </span>
                            ) : null}
                            <span
                              className={classes.commentText}
                              dangerouslySetInnerHTML={{ __html: reply.description }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </Box>}

      <Box className={classes.inputSection}>
        <Box className={classes.inputToolbar}>
          <IconButton onClick={() => setCanvasModal(true)}>
            <PaintBrush size={18} color="#f9f9f9" />
          </IconButton>
          <Box className={classes.timecodeBox}>
            <Clock size={14} />
            <span>{formatSequencerTime(currentTime)}</span>
            <Checkbox
              checked={isTimeStampChecked}
              size="small"
              style={{ padding: 0 }}
              onClick={() => setIsTimeStampChecked(!isTimeStampChecked)}
            />
          </Box>
          {/* <Box className={classes.tagVersion}>
            <span>Tag version</span>
            <Checkbox size="small" style={{ padding: 0 }} />
          </Box> */}
          <Button
            style={{
              fontSize: "11px",
              color: isSaveAsDraft ? "#F9F9F9" : "#EFB13A",
              background: isSaveAsDraft ? "#EFB13A2E" : "transparent",
              borderRadius: "5px",
              border: `1px solid ${isSaveAsDraft ? "#EFB13A4D" : "#EFB13A"}`,
              padding: "2px 6px",
              marginLeft: "8px",
            }}
            disableRipple
            disabled={!!repliedTo}
            onClick={() => setIsSaveAsDraft(!isSaveAsDraft)}
          >
            Save as draft
          </Button>
        </Box>

        {repliedTo ? (
          <Box className={classes.replyCard}>
            <Box className={classes.replyCardContent}>
              {repliedTo.timestamp ? (
                <span className={classes.timestamp}>
                  {getDisplayTime(repliedTo.timestamp)}
                </span>
              ) : null}
              <span
                className={classes.replyCardText}
                dangerouslySetInnerHTML={{ __html: repliedTo.description }}
              />
            </Box>
            <IconButton size="small" onClick={() => setRepliedTo(null)}>
              <X size={16} color="#606479" />
            </IconButton>
          </Box>
        ) : null}

        <Box className={classes.inputRow}>
          <Box className={`${classes.textInput} ${classes.postEditorInput}`}>
            <PostEditor
              placeholder={
                repliedTo ? "Write a reply..." : "Leave a comment here..."
              }
              mentions={mentionUsers}
              ref={mentionsRef}
              handleSubmit={() => {
                if (repliedTo || !isSaveAsDraft) {
                  handleMakeComment();
                } else {
                  handleSaveAsDraft();
                }
              }}
              resetPrevious={resetCommentText}
            />
          </Box>
          <Button
            className={classes.sendButton}
            onClick={repliedTo || !isSaveAsDraft ? handleMakeComment : handleSaveAsDraft}
            disableRipple
          >
            <PaperPlaneTilt size={20} />
          </Button>
        </Box>
      </Box>
      <Modal open={canvasModal} style={{ display: "flex", justifyContent: "center", alignItems: "center" }} onClose={() => setCanvasModal(false)}>
        <CanvasFrame
          isSequencerPlaylistViewer={true}
          imageRef={null} //state
          image={screenshot} //state
          assetID={selectedVersion?.id}
          toggle={() => setCanvasModal(!canvasModal)} //state
          fetchComments={fetchPlaylistComments} //function
          users={mentionUsers}
          progress={currentTime} //current time
          closeModal={() => setCanvasModal(false)}
          updateCommentsRefreshCounter={fetchPlaylistComments}
        />
      </Modal>
      <Modal open={attachmentModal} style={{ display: "flex", justifyContent: "center", alignItems: "center" }} onClose={() => setAttachmentModal(false)}>
        <Box style={{ position: "relative", height: "80%", width: "80%", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          <IconButton style={{ position: "absolute", top: "10px", right: "10px" }} size="small" onClick={() => setAttachmentModal(false)}>
            <X size={24} color="white" />
          </IconButton>
          <img style={{ height: "100%", width: "100%", objectFit: "cover" }} src={attachment || ""} alt="screenshot" />
        </Box>
      </Modal>
    </Box>
  );
};

export default SequencerMixPlaylistCommentsPanel;
